/**
 * SubagentsPanel — what this conversation delegated, where it ran, how it ended.
 *
 * Reads the durable ``subagent_runs`` ledger (ADR-0019), not the live event
 * stream. That ordering is the whole point: a panel fed only by events, backed
 * by a store that never learned a run ended, is precisely how upstream's
 * Background-tasks panel resurrects stopped tasks as "Running" after a refresh
 * (anthropics/claude-code#59456). Here the table is the truth and the
 * ``subagents_changed`` WS frame only saves a round trip — never polled.
 *
 * Two honesty rules the UI is built around:
 *
 * 1. **"Completed" is not proof of completion.** Background sub-agents die early
 *    in 14-30% of runs and are still reported completed
 *    (anthropics/claude-code#47936). The only real discriminator is whether a
 *    result came back, so a completed run with no result says exactly that
 *    rather than showing a green tick.
 * 2. **Stop and Dismiss are different actions.** Stop signals a live process
 *    and is only possible while its turn runs in that environment. Dismiss
 *    signals nothing — it records that a process is already gone, which is what
 *    releases the conversation for an environment move. Blurring them would
 *    give the user a kill button that quietly does nothing.
 */

import { useEffect, useState } from "react";

import { dismissSubagentRun, stopSubagentRun } from "@/api/subagentRuns";
import type { SubagentRun } from "@/api/types";
import { useChatStore } from "@/stores/chatStore";
import { useSubagentDrillStore } from "@/stores/subagentDrillStore";
import { Button } from "@/components/common/Button";
import { useToastStore } from "@/components/common/Toast";
import { formatTokens } from "@/components/chat/breakdownShared";

/** Status → (label, dot colour, text colour). ``orphaned`` reads as a warning
 *  rather than an error: nothing failed, the process simply stopped existing. */
const STATUS_META: Record<
  SubagentRun["status"],
  { label: string; dot: string; text: string }
> = {
  running: { label: "Running", dot: "bg-jove", text: "text-jove" },
  completed: { label: "Completed", dot: "bg-oxy", text: "text-oxy" },
  failed: { label: "Failed", dot: "bg-red-500", text: "text-red-500" },
  stopped: { label: "Stopped", dot: "bg-space-40", text: "text-space-40" },
  orphaned: { label: "Lost", dot: "bg-amber-500", text: "text-amber-600" },
};

/** Terminal statuses as icons, matching the running clock: the column stays a
 *  glanceable glyph, and the word lives in the tooltip + screen-reader text. */
function StatusGlyph({ status }: { status: SubagentRun["status"] }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2.5,
    className: "h-3.5 w-3.5",
    "aria-hidden": true,
  } as const;
  switch (status) {
    case "completed": // circled check
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9.5" />
          <path d="M8 12.5l2.7 2.7L16 9.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case "failed": // circled cross
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9.5" />
          <path d="M9 9l6 6M15 9l-6 6" strokeLinecap="round" />
        </svg>
      );
    case "stopped": // circled stop square
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9.5" />
          <rect x="8.75" y="8.75" width="6.5" height="6.5" rx="1" fill="currentColor" stroke="none" />
        </svg>
      );
    case "orphaned": // warning triangle
      return (
        <svg {...common}>
          <path d="M12 4L21 19.5H3L12 4z" strokeLinejoin="round" />
          <line x1="12" y1="10" x2="12" y2="14" strokeLinecap="round" />
          <line x1="12" y1="16.75" x2="12" y2="16.8" strokeLinecap="round" />
        </svg>
      );
    default:
      return null;
  }
}

/** ``desktop:5f9e8e47`` → ``Desktop``; ``cloud`` → ``Cloud``. The full id is
 *  noise in a list — the question being answered is "here or elsewhere?". */
function envLabel(env: string | null): string {
  if (!env) return "Unknown";
  if (env === "cloud") return "Cloud";
  if (env.startsWith("desktop:")) return "Desktop";
  return env;
}

/** A ticking clock for live runs: face static, minute hand sweeping. The
 *  built-in ``animate-spin`` keyframes are reused with a slower duration —
 *  arbitrary ``animate-[…]`` values can't be trusted to emit the named
 *  keyframes, and this webview (Chromium 87) is unforgiving about it. */
function RunningClock() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      className="h-3.5 w-3.5"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9.5" />
      <line x1="12" y1="12" x2="16" y2="12" strokeLinecap="round" />
      <g
        className="animate-spin"
        style={{ animationDuration: "2s", transformOrigin: "12px 12px" }}
      >
        <line x1="12" y1="12" x2="12" y2="6.5" strokeLinecap="round" />
      </g>
    </svg>
  );
}

/** Environment as an icon: the row answers "here or elsewhere?" at a glance;
 *  the word is kept in the tooltip and for screen readers. Unrecognised
 *  environments stay as text — an icon we'd be guessing at explains nothing. */
function EnvBadge({ env }: { env: string | null }) {
  const label = envLabel(env);
  if (env === "cloud") {
    return (
      <span title={label} className="inline-flex items-center" aria-label={label}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-3.5 w-3.5"
          aria-hidden="true"
        >
          <path d="M17.5 18.5H7a4.5 4.5 0 1 1 .6-8.96 6 6 0 0 1 11.4 1.9 3.5 3.5 0 0 1-1.5 7.06z" />
        </svg>
      </span>
    );
  }
  if (env?.startsWith("desktop:")) {
    return (
      <span title={label} className="inline-flex items-center" aria-label={label}>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="h-3.5 w-3.5"
          aria-hidden="true"
        >
          <rect x="3" y="4.5" width="18" height="12.5" rx="1.5" />
          <line x1="9" y1="20" x2="15" y2="20" strokeLinecap="round" />
          <line x1="12" y1="17" x2="12" y2="20" />
        </svg>
      </span>
    );
  }
  return <span>{label}</span>;
}

/** How long a live run may say nothing before the panel stops implying progress.
 *  Informational only — nothing is closed here; the server's own sweep is the
 *  thing that ends a run, on a deliberately longer clock. */
const STALL_HINT_MS = 90_000;

/** Live elapsed-time ticker for a running row. Terminal rows show the stored
 *  ``duration_ms``; a live one has nothing yet, so the panel counts from
 *  ``started_at`` itself — one interval per live row, dropped on unmount. */
function useElapsedMs(startedAt: string, live: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [live]);
  if (!live) return 0;
  const started = Date.parse(startedAt);
  return Number.isNaN(started) ? 0 : Math.max(0, now - started);
}

function formatDuration(ms: number): string {
  const total = Math.max(0, Math.round(ms / 1000));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  return `${m}m ${total % 60}s`;
}

/** The one-line truth about how a run ended, result-evidence included. */
function outcomeOf(run: SubagentRun): string | null {
  if (run.status === "running") return null;
  if (run.status === "completed" && !run.result_seen) {
    // Not pedantry: this is the documented signature of a sub-agent that was
    // killed mid-flight and reported as completed anyway.
    return "Completed — but returned no result";
  }
  return null;
}

function RunRow({ run }: { run: SubagentRun }) {
  const conversationId = useChatStore((s) => s.activeConversationId);
  const drillInto = useSubagentDrillStore((s) => s.open);
  const toast = useToastStore((s) => s.add);
  const [busy, setBusy] = useState(false);

  const meta = STATUS_META[run.status] ?? STATUS_META.stopped;
  const isLive = run.status === "running";
  const caveat = outcomeOf(run);
  const elapsedMs = useElapsedMs(run.started_at, isLive);
  // Time since the run last SAID anything, which is a different question from
  // how long it has been going. A bare climbing timer reads as progress even
  // when nothing has happened for minutes — a sub-agent went quiet mid-task and
  // the panel kept counting confidently for 7 minutes with no way to tell work
  // from a hang (2026-08-03). Mirrors STALL_HINT_SECS server-side.
  const quietMs = useElapsedMs(run.updated_at || run.started_at, isLive);
  const looksStalled = isLive && quietMs > STALL_HINT_MS;

  // Surface the server's own refusal text: it explains *why* (already finished,
  // running on another environment, no task id) far better than a generic
  // failure, and those reasons are the interesting part of this feature.
  const act = async (fn: () => Promise<unknown>, okMsg: string) => {
    if (!conversationId) return;
    setBusy(true);
    try {
      await fn();
      toast(okMsg, "success");
    } catch (err) {
      toast(err instanceof Error ? err.message : "That didn't work", "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-space-30 bg-space-10 p-3 space-y-2">
      <div className="flex items-start gap-2">
        <span
          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${meta.dot} ${
            isLive ? "animate-pulse" : ""
          }`}
          aria-hidden="true"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="truncate text-sm font-medium text-space">
              {run.description || run.agent_type || "Sub-agent"}
            </span>
            <span
              title={meta.label}
              className={`inline-flex shrink-0 items-center ${meta.text}`}
            >
              {isLive ? <RunningClock /> : <StatusGlyph status={run.status} />}
              <span className="sr-only">{meta.label}</span>
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-space-40">
            {run.agent_type && run.description ? <span>{run.agent_type}</span> : null}
            <EnvBadge env={run.environment} />
            {run.backgrounded ? <span>Background</span> : null}
            {isLive && elapsedMs > 0 ? <span>{formatDuration(elapsedMs)}</span> : null}
            {looksStalled ? (
              <span className="text-amber-600" title="No progress reported since then">
                no progress for {formatDuration(quietMs)}
              </span>
            ) : null}
            {!isLive && run.duration_ms > 0 ? <span>{formatDuration(run.duration_ms)}</span> : null}
            {run.total_tokens > 0 ? <span>{formatTokens(run.total_tokens)} tok</span> : null}
            {isLive && run.last_tool_name ? <span>{run.last_tool_name}</span> : null}
          </div>
          {caveat ? (
            <div className="mt-1 text-xs text-amber-600">{caveat}</div>
          ) : null}
          {run.status === "orphaned" && run.summary ? (
            <div className="mt-1 text-xs text-space-40">{run.summary}</div>
          ) : null}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1.5 pl-4">
        <Button
          variant="ghost"
          size="sm"
          className="!px-2.5 !py-1 !text-xs"
          onClick={() => drillInto(run.tool_use_id)}
        >
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            className="h-3.5 w-3.5"
            aria-hidden="true"
          >
            <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" strokeLinejoin="round" />
            <circle cx="12" cy="12" r="2.75" />
          </svg>
          View activity
        </Button>
        {isLive ? (
          <Button
            variant="danger"
            size="sm"
            className="!px-2.5 !py-1 !text-xs"
            disabled={busy || !conversationId}
            onClick={() =>
              act(
                () => stopSubagentRun(conversationId!, run.tool_use_id),
                "Stop requested",
              )
            }
            title="Signal this sub-agent to stop. Only possible while its turn is running here."
          >
            <svg viewBox="0 0 24 24" className="h-3 w-3" aria-hidden="true">
              <rect x="6" y="6" width="12" height="12" rx="2" fill="currentColor" />
            </svg>
            Stop
          </Button>
        ) : null}
        {isLive ? (
          <Button
            variant="ghost"
            size="sm"
            className="!px-2.5 !py-1 !text-xs"
            disabled={busy || !conversationId}
            onClick={() =>
              act(
                () => dismissSubagentRun(conversationId!, run.tool_use_id),
                "Marked as lost",
              )
            }
            title="Mark this run as lost. Stops nothing — use it when the process is already gone and the conversation is stuck."
          >
            Dismiss
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function SubagentsPanel() {
  const runs = useChatStore((s) => s.subagentRuns);

  const active = runs.filter((r) => r.status === "running");
  const past = runs.filter((r) => r.status !== "running");

  if (runs.length === 0) {
    return (
      <div className="p-4 text-sm text-space-40">
        This conversation hasn't delegated to any sub-agents yet.
      </div>
    );
  }

  return (
    <div className="space-y-4 p-3">
      {active.length > 0 ? (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-space-40">
            Active · {active.length}
          </h3>
          {active.map((r) => (
            <RunRow key={r.tool_use_id} run={r} />
          ))}
        </section>
      ) : null}

      {past.length > 0 ? (
        <section className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-space-40">
            Past · {past.length}
          </h3>
          {past.map((r) => (
            <RunRow key={r.tool_use_id} run={r} />
          ))}
        </section>
      ) : null}
    </div>
  );
}
